import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, X, Plus, Trash2 } from "lucide-react";
import { DocumentType, DocumentTypeDeliveryTime } from "@/models";
import { documentTypeApi, customerTypeApi, managementCompanyApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { cmsApi } from "@/services/api/cmsApi";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import {
  validateExpirationSettings,
  formatExpirationSettings,
} from "@/utils/expirationCalculator";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  source: z.enum(["file", "platform"]).default("platform"),
  default_expiration_type: z.enum(["days", "date", "annual"]).default("days"),
  default_expiration_days: z.coerce.number().nullish(),
  default_expiration_day: z.coerce.number().min(1).max(31).nullish(),
  default_expiration_month: z.coerce.number().min(1).max(12).nullish(),
  default_expiration_year: z.coerce.number().min(2024).nullish(),
  // New fields for expired/missing document requests (file source only)
  allow_expired_requests: z.boolean().default(false),
  handling_time_days: z.coerce.number().min(1).nullish(),

  accept_custom_form: z.boolean().default(false),
  has_custom_delivery_times: z.boolean().default(false),
  generates_output_document: z.boolean().default(false),
  template_id: z.coerce.number().nullish(),
  // Scope: null = platform-wide, a management company id = that MC's own.
  management_company_id: z.coerce.number().nullish(),
  delivery_times: z
    .array(
      z.object({
        id: z.number().optional(), // Include ID for updates
        name: z.string().min(1, "Name is required"),
        days: z.coerce.number().min(1, "Days must be at least 1"),
        is_default: z.boolean().optional(),
      })
    )
    .optional(),
  customer_types: z.array(z.number()).optional(),
  // New fields for document update after submission
  is_updatable: z.boolean().default(false),
  min_update_processing_time: z.coerce.number().min(1).nullish(),
});

type FormValues = z.infer<typeof formSchema>;

interface DocumentTypeFormProps {
  documentType?: DocumentType;
  onSuccess?: () => void;
  onCancel?: () => void;
  formId?: string;
  /** Pre-set scope for a NEW type (e.g. created from a MC's own page). */
  defaultManagementCompanyId?: number | null;
}

const DocumentTypeForm = ({
  documentType,
  onSuccess,
  onCancel,
  formId = "document-type-form",
  defaultManagementCompanyId,
}: DocumentTypeFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedRole } = useAuth();

  const { data: customerTypes = [] } = useQuery({
    queryKey: ["customerTypes"],
    queryFn: () => customerTypeApi.list(),
  });

  const { data: managementCompanies = [] } = useQuery({
    queryKey: ["managementCompanies"],
    queryFn: () => managementCompanyApi.list(),
  });

  const { data: templateFolders = [] } = useQuery({
    queryKey: ["templateFolders"],
    queryFn: () => cmsApi.getTemplateFolders(),
  });

  // Only offer templates compatible with THIS type (plus generic folders and
  // the one currently selected), within the caller's scope (8-Sep).
  const compatibleTemplateFolders = (templateFolders as any[]).filter((f) => {
    const scopeOk =
      selectedRole?.type === "management_company"
        ? f.management_company_id == null ||
          Number(f.management_company_id) === Number(selectedRole.entityId)
        : true;
    const compatOk =
      f.document_type_id == null ||
      (documentType?.id && Number(f.document_type_id) === Number(documentType.id)) ||
      (documentType?.template_id != null &&
        Number(f.id) === Number(documentType.template_id));
    return scopeOk && compatOk;
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: documentType?.name || "",
      source: documentType?.source || "platform",
      default_expiration_type:
        (documentType?.source === "file"
          ? documentType.default_expiration_type
          : undefined) || "days",
      default_expiration_days:
        (documentType?.source === "file"
          ? documentType.default_expiration_days
          : undefined) || null,
      default_expiration_day:
        (documentType?.source === "file"
          ? documentType.default_expiration_day
          : undefined) || null,
      default_expiration_month:
        (documentType?.source === "file"
          ? documentType.default_expiration_month
          : undefined) || null,
      default_expiration_year:
        (documentType?.source === "file"
          ? documentType.default_expiration_year
          : undefined) || null,
      allow_expired_requests:
        (documentType?.source === "file"
          ? documentType.allow_expired_requests
          : false) || false,
      handling_time_days:
        (documentType?.source === "file"
          ? documentType.handling_time_days
          : undefined) || null,
      accept_custom_form:
        (documentType?.source === "platform"
          ? documentType.accept_custom_form
          : false) || false,
      has_custom_delivery_times:
        (documentType?.source === "platform"
          ? documentType.has_custom_delivery_times
          : false) || false,
      generates_output_document:
        documentType?.generates_output_document || false,
      template_id: documentType?.template_id || null,
      // Editing keeps the type's owner; a NEW type born from a management
      // company context (or that company's own page) belongs to it (8-Sep).
      management_company_id: documentType
        ? (documentType as any).management_company_id ?? null
        : defaultManagementCompanyId ??
          (selectedRole?.type === "management_company"
            ? Number(selectedRole.entityId) || null
            : null),
      delivery_times: (documentType?.source === "platform"
        ? documentType.delivery_times
        : undefined) || [
        {
          name: "Standard",
          days: 10,
          is_default: true,
        } as DocumentTypeDeliveryTime,
      ],
      customer_types: documentType?.customer_types || [],
      is_updatable:
        (documentType?.source === "platform"
          ? documentType.is_updatable
          : false) || false,
      min_update_processing_time:
        (documentType?.source === "platform"
          ? documentType.min_update_processing_time
          : undefined) || null,
    },
  });

  useEffect(() => {
    if (documentType) {
      console.log("Setting form values from document type:", documentType);
      form.reset({
        name: documentType.name,
        source: documentType.source,
        default_expiration_type:
          (documentType.source === "file"
            ? documentType.default_expiration_type
            : undefined) || "days",
        default_expiration_days:
          (documentType.source === "file"
            ? documentType.default_expiration_days
            : undefined) || null,
        default_expiration_day:
          (documentType.source === "file"
            ? documentType.default_expiration_day
            : undefined) || null,
        default_expiration_month:
          (documentType.source === "file"
            ? documentType.default_expiration_month
            : undefined) || null,
        default_expiration_year:
          (documentType.source === "file"
            ? documentType.default_expiration_year
            : undefined) || null,
        allow_expired_requests:
          (documentType.source === "file"
            ? documentType.allow_expired_requests
            : false) || false,
        handling_time_days:
          (documentType.source === "file"
            ? documentType.handling_time_days
            : undefined) || null,
        accept_custom_form:
          (documentType.source === "platform"
            ? documentType.accept_custom_form
            : false) || false,
        has_custom_delivery_times:
          (documentType.source === "platform"
            ? documentType.has_custom_delivery_times
            : false) || false,
        generates_output_document:
          documentType.generates_output_document || false,
        template_id: documentType.template_id || null,
        management_company_id:
          (documentType as any).management_company_id ?? null,
        delivery_times: (documentType.source === "platform"
          ? documentType.delivery_times
          : undefined) || [
          {
            name: "Standard",
            days: 10,
            is_default: true,
          } as DocumentTypeDeliveryTime,
        ],
        customer_types: documentType.customer_types || [],
        is_updatable:
          (documentType.source === "platform"
            ? documentType.is_updatable
            : false) || false,
        min_update_processing_time:
          (documentType.source === "platform"
            ? documentType.min_update_processing_time
            : undefined) || null,
      });
    }
  }, [documentType, form]);

  const onSubmit = async (data: FormValues) => {
    try {
      console.log("Submitting form data:", data);

      // Transform delivery times to match the expected interface
      // For updates, preserve the IDs so backend can update instead of delete/recreate
      const transformedDeliveryTimes =
        data.delivery_times?.map((dt) => ({
          ...((dt as any).id && { id: (dt as any).id }), // Include ID if it exists (for updates)
          name: dt.name || "",
          days: dt.days || 1,
        })) || [];

      if (documentType) {
        await documentTypeApi.update(documentType.id, {
          ...data,
          delivery_times: transformedDeliveryTimes,
          customer_types: data.customer_types,
          is_updatable: data.is_updatable,
          min_update_processing_time: data.min_update_processing_time,
        } as any);
      } else {
        const newDocTypeData = {
          name: data.name,
          source: data.source,
          default_expiration_type: data.default_expiration_type,
          default_expiration_days: data.default_expiration_days,
          default_expiration_day: data.default_expiration_day,
          default_expiration_month: data.default_expiration_month,
          default_expiration_year: data.default_expiration_year,
          allow_expired_requests: data.allow_expired_requests,
          handling_time_days: data.handling_time_days,
          accept_custom_form: data.accept_custom_form,
          has_custom_delivery_times: data.has_custom_delivery_times,
          generates_output_document: data.generates_output_document,
          template_id: data.template_id,
          management_company_id: data.management_company_id ?? null,
          delivery_times: transformedDeliveryTimes,
          customer_types: data.customer_types,
          is_updatable: data.is_updatable,
          min_update_processing_time: data.min_update_processing_time,
        };

        await documentTypeApi.create(newDocTypeData as any);
      }

      if (onSuccess) {
        console.log("Calling onSuccess callback");
        onSuccess();
      } else {
        // Default redirect if no onSuccess is provided
        navigate("/backoffice/settings?tab=document-types");
      }
    } catch (error: any) {
      console.error("Error submitting form:", error);
      toast({
        variant: "destructive",
        title: "Error saving document type",
        description:
          error.message || "There was an error saving the document type.",
      });
    }
  };

  const isLoading = form.formState.isSubmitting;

  const customerTypesById = customerTypes.reduce((acc, type) => {
    acc[type.id] = type.name;
    return acc;
  }, {} as Record<number, string>);

  const addCustomerTypeToSelection = (typeId: number) => {
    const currentTypes = form.getValues("customer_types") || [];
    if (!currentTypes.includes(typeId)) {
      form.setValue("customer_types", [...currentTypes, typeId]);
    }
  };

  const removeCustomerTypeFromSelection = (typeId: number) => {
    const currentTypes = form.getValues("customer_types") || [];
    form.setValue(
      "customer_types",
      currentTypes.filter((id) => id !== typeId)
    );
  };

  const addDeliveryTime = () => {
    const currentDeliveryTimes = form.getValues("delivery_times") || [];
    form.setValue("delivery_times", [
      ...currentDeliveryTimes,
      { name: "", days: 1, is_default: false },
    ]);
  };

  const removeDeliveryTime = (index: number) => {
    const currentDeliveryTimes = form.getValues("delivery_times") || [];
    form.setValue(
      "delivery_times",
      currentDeliveryTimes.filter((_, i) => i !== index)
    );
  };

  const updateDeliveryTime = (
    index: number,
    field: "name" | "days",
    value: string | number
  ) => {
    const currentDeliveryTimes = form.getValues("delivery_times") || [];
    const updatedDeliveryTimes = [...currentDeliveryTimes];
    updatedDeliveryTimes[index] = {
      ...updatedDeliveryTimes[index],
      [field]: value,
    };
    form.setValue("delivery_times", updatedDeliveryTimes);
  };

  useEffect(() => {
    console.log("Current customer_types value:", form.watch("customer_types"));
  }, [form.watch("customer_types")]);

  // Reset accept_custom_form when source changes to 'file'
  useEffect(() => {
    const source = form.watch("source");
    if (source === "file") {
      form.setValue("accept_custom_form", false);
      // Reset custom delivery times when source changes to 'file'
      form.setValue("has_custom_delivery_times", false);
      form.setValue("delivery_times", [
        {
          name: "Standard",
          days: 10,
          is_default: true,
        } as DocumentTypeDeliveryTime,
      ]);
      // Reset update settings when source changes to 'file'
      form.setValue("is_updatable", false);
      form.setValue("min_update_processing_time", null);
    } else if (source === "platform") {
      // Reset expiration settings when source changes to 'platform'
      form.setValue("default_expiration_type", "days");
      form.setValue("default_expiration_days", null);
      form.setValue("default_expiration_day", null);
      form.setValue("default_expiration_month", null);
      form.setValue("default_expiration_year", null);
      // Reset expired request settings when source changes to 'platform'
      form.setValue("allow_expired_requests", false);
      form.setValue("handling_time_days", null);
    }
  }, [form.watch("source"), form]);

  // Reset delivery_times when has_custom_delivery_times is unchecked
  useEffect(() => {
    const hasCustomDeliveryTimes = form.watch("has_custom_delivery_times");
    if (!hasCustomDeliveryTimes) {
      form.setValue("delivery_times", [
        {
          name: "Standard",
          days: 10,
          is_default: true,
        } as DocumentTypeDeliveryTime,
      ]);
    }
  }, [form.watch("has_custom_delivery_times"), form]);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6"
      >
        <Card>
          <CardHeader>
            <CardTitle>Document Type Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Document type name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="source"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select source type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="platform">
                        Generated by the platform
                      </SelectItem>
                      <SelectItem value="file">Uploaded file</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    How the document is produced — a form the platform turns
                    into an output, or a file someone uploads. (Unrelated to
                    the Scope below.)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="management_company_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Scope</FormLabel>
                  <Select
                    onValueChange={(v) =>
                      field.onChange(v === "platform" ? null : Number(v))
                    }
                    value={
                      field.value == null ? "platform" : String(field.value)
                    }
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Who owns this type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="platform">
                        Platform (all management companies)
                      </SelectItem>
                      {managementCompanies.map((mc: any) => (
                        <SelectItem key={mc.id} value={String(mc.id)}>
                          {mc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Platform types are shared with every management company; a
                    scoped type belongs to that company only.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Only show accept_custom_form for platform source */}
            {form.watch("source") === "platform" && (
              <FormField
                control={form.control}
                name="accept_custom_form"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Accept Custom Form</FormLabel>
                      <FormDescription>
                        Allow requestors to upload their own custom forms for
                        this document type instead of filling out the platform
                        form.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            )}

            {/* Custom Delivery Times Section - Only for platform source */}
            {form.watch("source") === "platform" && (
              <>
                <FormField
                  control={form.control}
                  name="has_custom_delivery_times"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Use Custom Delivery Times</FormLabel>
                        <FormDescription>
                          Define custom delivery time options instead of using
                          default times.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                {form.watch("has_custom_delivery_times") && (
                  <FormField
                    control={form.control}
                    name="delivery_times"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Delivery Time Options</FormLabel>
                        <FormDescription>
                          Define the available delivery time options for this
                          document type.
                        </FormDescription>
                        <div className="space-y-4">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Days</TableHead>
                                <TableHead className="w-[100px]">
                                  Actions
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(field.value || []).map(
                                (deliveryTime, index) => (
                                  <TableRow key={index}>
                                    <TableCell>
                                      {deliveryTime.is_default ? (
                                        <div className="flex items-center space-x-2">
                                          <Input
                                            value={deliveryTime.name}
                                            disabled
                                            className="bg-gray-50"
                                          />
                                          <Badge
                                            variant="secondary"
                                            className="text-xs"
                                          >
                                            Default
                                          </Badge>
                                        </div>
                                      ) : (
                                        <Input
                                          value={deliveryTime.name}
                                          onChange={(e) =>
                                            updateDeliveryTime(
                                              index,
                                              "name",
                                              e.target.value
                                            )
                                          }
                                          placeholder="e.g., Rush, Express"
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Input
                                        type="number"
                                        min={1}
                                        value={deliveryTime.days}
                                        onChange={(e) =>
                                          updateDeliveryTime(
                                            index,
                                            "days",
                                            parseInt(e.target.value) || 1
                                          )
                                        }
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          removeDeliveryTime(index)
                                        }
                                        disabled={
                                          deliveryTime.is_default ||
                                          (field.value || []).length <= 1
                                        }
                                        title={
                                          deliveryTime.is_default
                                            ? "Cannot remove default delivery time"
                                            : "Remove delivery time"
                                        }
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                )
                              )}
                            </TableBody>
                          </Table>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addDeliveryTime}
                            className="w-full"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Delivery Time Option
                          </Button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Document Update Settings - Only for platform source */}
                <div className="border-t pt-4 mt-6">
                  <h4 className="font-medium text-sm mb-4">
                    Document Update Settings
                  </h4>

                  <FormField
                    control={form.control}
                    name="is_updatable"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Allow Updates After Submission</FormLabel>
                          <FormDescription>
                            Enable this to allow updating documents of this type
                            after they have been submitted.
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  {form.watch("is_updatable") && (
                    <FormField
                      control={form.control}
                      name="min_update_processing_time"
                      render={({ field }) => (
                        <FormItem className="mt-4">
                          <FormLabel>
                            Minimum Update Processing Time (days)
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={1}
                              placeholder="Enter minimum days required to process updates"
                              value={field.value === null ? "" : field.value}
                              onChange={(e) => {
                                const value =
                                  e.target.value === ""
                                    ? null
                                    : parseInt(e.target.value);
                                field.onChange(value);
                              }}
                            />
                          </FormControl>
                          <FormDescription>
                            Minimum number of days required to process document
                            updates for this type.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </>
            )}

            {/* Expiration Settings Section - Only for file source */}
            {form.watch("source") === "file" && (
              <>
                {/* Expiration Type Selection */}
                <FormField
                  control={form.control}
                  name="default_expiration_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default expiration schedule</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select expiration type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="days">Days</SelectItem>
                          <SelectItem value="annual">
                            Annual (Month & Day)
                          </SelectItem>
                          <SelectItem value="date">
                            Specific Date (Year, Month & Day)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Choose how the expiration should be set for this
                        document type.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Conditionally render expiration fields */}
                {form.watch("default_expiration_type") === "days" && (
                  <FormField
                    control={form.control}
                    name="default_expiration_days"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default Expiration Time (days)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            placeholder="Leave empty for no expiration"
                            value={field.value === null ? "" : field.value}
                            onChange={(e) => {
                              const value =
                                e.target.value === ""
                                  ? null
                                  : parseInt(e.target.value);
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {form.watch("default_expiration_type") === "annual" && (
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="default_expiration_month"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Expiration Month</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={(val) =>
                                field.onChange(val ? parseInt(val) : null)
                              }
                              value={field.value ? String(field.value) : ""}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select month" />
                              </SelectTrigger>
                              <SelectContent>
                                {monthNames.map((name, idx) => (
                                  <SelectItem
                                    key={idx + 1}
                                    value={String(idx + 1)}
                                  >
                                    {name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="default_expiration_day"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Expiration Day</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={1}
                              max={31}
                              placeholder="Day"
                              value={field.value === null ? "" : field.value}
                              onChange={(e) => {
                                const value =
                                  e.target.value === ""
                                    ? null
                                    : parseInt(e.target.value);
                                field.onChange(value);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {form.watch("default_expiration_type") === "date" && (
                  <div className="grid grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="default_expiration_year"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Expiration Year</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={2024}
                              placeholder="Year"
                              value={field.value === null ? "" : field.value}
                              onChange={(e) => {
                                const value =
                                  e.target.value === ""
                                    ? null
                                    : parseInt(e.target.value);
                                field.onChange(value);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="default_expiration_month"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Expiration Month</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={(val) =>
                                field.onChange(val ? parseInt(val) : null)
                              }
                              value={field.value ? String(field.value) : ""}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select month" />
                              </SelectTrigger>
                              <SelectContent>
                                {monthNames.map((name, idx) => (
                                  <SelectItem
                                    key={idx + 1}
                                    value={String(idx + 1)}
                                  >
                                    {name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="default_expiration_day"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Expiration Day</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={1}
                              max={31}
                              placeholder="Day"
                              value={field.value === null ? "" : field.value}
                              onChange={(e) => {
                                const value =
                                  e.target.value === ""
                                    ? null
                                    : parseInt(e.target.value);
                                field.onChange(value);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* Expired/Missing Document Request Settings */}
                <div className="border-t pt-4 mt-6">
                  <h4 className="font-medium text-sm mb-4">
                    Expired/Missing Document Requests
                  </h4>

                  <FormField
                    control={form.control}
                    name="allow_expired_requests"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Allow Requests for Expired/Missing Documents
                          </FormLabel>
                          <FormDescription>
                            Enable this to allow requests for documents that are
                            expired or not yet uploaded.
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  {form.watch("allow_expired_requests") && (
                    <FormField
                      control={form.control}
                      name="handling_time_days"
                      render={({ field }) => (
                        <FormItem className="mt-4">
                          <FormLabel>Handling Time (days)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={1}
                              placeholder="Enter number of days needed to handle the request"
                              value={field.value === null ? "" : field.value}
                              onChange={(e) => {
                                const value =
                                  e.target.value === ""
                                    ? null
                                    : parseInt(e.target.value);
                                field.onChange(value);
                              }}
                            />
                          </FormControl>
                          <FormDescription>
                            Number of days needed to process and upload the
                            document when requested.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </>
            )}

            <FormField
              control={form.control}
              name="generates_output_document"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Generates an Output Document</FormLabel>
                    <FormDescription>
                      Enable this if this document type should generate a
                      formatted output document when processed. When enabled,
                      the system will create a structured document based on the
                      input data using customizable templates.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {/* Template Selection - Only show when generates_output_document is true */}
            {form.watch("generates_output_document") && (
              <FormField
                control={form.control}
                name="template_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Output Template</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(value ? parseInt(value) : null)
                      }
                      value={field.value ? field.value.toString() : ""}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a template for document output" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {compatibleTemplateFolders.length === 0 && (
                          <span className="text-muted-foreground">
                            No templates found
                          </span>
                        )}
                        {compatibleTemplateFolders.map((template) => (
                          <SelectItem
                            key={template.id}
                            value={template.id.toString()}
                          >
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Choose a template from the CMS that will be used to format
                      the output document. Templates can be managed in the
                      Content Management section.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="customer_types"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer Types</FormLabel>
                  <FormDescription>
                    Select customer types applicable to this document type
                  </FormDescription>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {customerTypes.map((type) => {
                      const isSelected = field.value?.includes(type.id);
                      return (
                        <Badge
                          key={type.id}
                          variant={isSelected ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => {
                            isSelected
                              ? removeCustomerTypeFromSelection(type.id)
                              : addCustomerTypeToSelection(type.id);
                          }}
                        >
                          {type.name}
                          {isSelected && <X className="ml-1 h-3 w-3" />}
                        </Badge>
                      );
                    })}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4">
          <Button
            variant="outline"
            onClick={
              onCancel ||
              (() => navigate("/backoffice/settings?tab=document-types"))
            }
            type="button"
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {documentType ? "Update Document Type" : "Create Document Type"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default DocumentTypeForm;
